import { useEffect, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authEntity, clearAuth } from "../../entities/auth.entity";
import LanguageBox from "../organisms/LanguageBox";
import SupportWidget from "../organisms/SupportWidget";
import { fetchPlatformPublic } from "../../entities/platform.entity";
import type { AuthLayoutProps } from "./template.types";

const AuthLayout = ({
  allowAuthenticated = false,
  children,
  pageTitle = "PIF | Login",
  setAutoHeight = false,
}: AuthLayoutProps) => {
  const auth = authEntity.use();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    // Match the document surface to auth pages during elastic viewport scroll.
    document.documentElement.classList.add("auth-page-surface");
    document.body.classList.add("auth-page-surface");

    return () => {
      document.documentElement.classList.remove("auth-page-surface");
      document.body.classList.remove("auth-page-surface");
    };
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;
    if (
      auth &&
      (currentPath.includes("partner-invite") ||
        currentPath.includes("invitation"))
    ) {
      clearAuth();
      navigate(currentPath);
      return;
    }
    if (auth && !allowAuthenticated) navigate("/dashboard", { replace: true });

    (async () => {
      await fetchPlatformPublic();
    })();
  }, []);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <div
      className={`${
        setAutoHeight
          ? "h-[100dvh] overflow-hidden"
          : "h-screen touch-pan-y overflow-y-auto overscroll-none"
      } flex w-full max-w-full flex-col items-center overflow-x-hidden bg-dark-teal py-12 text-white`}
    >
      <div className="mb-10 w-full shrink-0">
        <div className="flex items-center justify-between w-11/12 mx-auto md:w-10/12 max-w-screen-2xl">
          <div className="flex items-center gap-2 shrink-0">
            <img
              {...{ src: "/svgs/pif-logo.svg", alt: "PIF", loading: "lazy" }}
              className="w-auto h-14"
            />
            <span className="font-bold md:text-lg opacity-90">
              PAY IT FORWARD
            </span>
          </div>
          <LanguageBox />
        </div>
      </div>
      {setAutoHeight ? (
        <div
          className="flex min-h-0 w-full flex-1 touch-pan-y flex-col items-center overflow-x-hidden overflow-y-auto overscroll-none"
          data-testid="auth-scroll-content"
        >
          {children}
        </div>
      ) : (
        children
      )}
      <SupportWidget />
    </div>
  );
};

export default AuthLayout;
